/* ============================================================
   Tienda de mascotas · componentes JS
   - <pet-carousel>: carrusel deslizable (scroll-snap) con puntos,
     flechas, autoplay opcional y soporte del editor de temas.
   - <pet-bundle>: "Cómpralos juntos": suma precios y añade varios
     productos al carrito de una vez (abre el carrito lateral).
   ============================================================ */

(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class PetCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.index = 0;
      if (this.slides.length < 2) return;

      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.index = this.slides.indexOf(entry.target);
              this.update();
            }
          });
        },
        { root: this.track, threshold: [0.6] }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));

      this.delay = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.autoplay = this.hasAttribute('data-autoplay') && !reduceMotion.matches;
      if (this.autoplay) {
        const pause = () => this.stop();
        const resume = () => this.play();
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.track.addEventListener('pointerdown', pause, { passive: true });
        document.addEventListener('visibilitychange', () => (document.hidden ? pause() : resume()));
        this.play();
      }

      // Editor de temas: al seleccionar un bloque, mostrar esa diapositiva
      this.onBlockSelect = (event) => {
        const i = this.slides.findIndex((s) => s.dataset.blockId === event.detail.blockId);
        if (i > -1) {
          this.stop();
          this.goTo(i);
        }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.update();
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    play() {
      if (!this.autoplay) return;
      this.stop();
      this.timer = setInterval(() => this.goTo(this.index + 1), this.delay);
    }

    stop() {
      clearInterval(this.timer);
    }

    goTo(i) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.track.scrollTo({
        left: this.slides[this.index].offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.update();
    }

    update() {
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.slides.forEach((slide, i) => {
        slide.toggleAttribute('inert', i !== this.index);
      });
    }
  }
  if (!customElements.get('pet-carousel')) customElements.define('pet-carousel', PetCarousel);

  class PetBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-bundle-add]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.countEl = this.querySelector('[data-bundle-count]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
      this.querySelectorAll('[data-bundle-check]').forEach((el) => el.addEventListener('change', () => this.refresh()));
      this.button?.addEventListener('click', () => this.add());
      this.refresh();
    }

    checkedItems() {
      return Array.from(this.querySelectorAll('[data-bundle-check]')).filter((el) => el.checked);
    }

    formatMoney(cents) {
      const value = (cents / 100).toFixed(2);
      const [int, dec] = value.split('.');
      const withDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withDots},${dec}`)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withDots)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
    }

    refresh() {
      const items = this.checkedItems();
      const total = items.reduce((sum, el) => sum + parseInt(el.dataset.price || '0', 10), 0);
      const pct = parseFloat(this.dataset.discount) || 0;
      // Tramos fijos (p. ej. 2 → −3 €, 3 → −6,95 €): se aplica el mayor que cumpla la cantidad
      const tiers = (this.dataset.tiers || '').split(',').map((t) => t.split('|').map((n) => parseInt(n, 10))).filter((t) => t[0] && t[1]);
      const tier = tiers.filter((t) => items.length >= t[0]).reduce((a, t) => Math.max(a, t[1]), 0);
      const discounted = tier > 0 ? total - tier : pct > 0 && items.length >= 2 ? Math.round((total * (100 - pct)) / 100) : total;
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(discounted);
      const was = this.querySelector('[data-bundle-was]');
      const saving = this.querySelector('[data-bundle-saving]');
      if (was) {
        was.hidden = discounted === total;
        was.textContent = this.formatMoney(total);
      }
      if (saving) {
        saving.hidden = discounted === total;
        const amount = saving.querySelector('[data-bundle-saving-amount]');
        if (amount) amount.textContent = this.formatMoney(total - discounted);
      }
      if (this.countEl) this.countEl.textContent = items.length;
      if (this.button) this.button.disabled = items.length === 0;
      this.querySelectorAll('[data-bundle-item]').forEach((card) => {
        const check = card.querySelector('[data-bundle-check]');
        card.classList.toggle('is-off', check && !check.checked);
      });
    }

    currentMainVariant() {
      const input = document.querySelector('product-form form[action*="/cart/add"] [name="id"]');
      return input ? input.value : null;
    }

    async add() {
      const items = this.checkedItems().map((el) => ({
        id: el.dataset.main === 'true' ? this.currentMainVariant() || el.value : el.value,
        quantity: 1,
      }));
      if (!items.length) return;
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      if (this.errorEl) this.errorEl.hidden = true;
      const cart = document.querySelector('cart-drawer');
      try {
        const res = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: cart ? cart.getSectionsToRender().map((s) => s.id) : [],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');
        if (cart && typeof cart.renderContents === 'function') {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location.href = window.routes.cart_url;
        }
        if (window.petTrack) window.petTrack('AddToCart', { content_type: 'product_group', num_items: items.length });
      } catch (e) {
        if (this.errorEl) {
          this.errorEl.textContent = e.message || 'No se ha podido añadir. Inténtalo de nuevo.';
          this.errorEl.hidden = false;
        }
      } finally {
        this.button.removeAttribute('aria-busy');
        this.button.disabled = this.checkedItems().length === 0;
      }
    }
  }
  if (!customElements.get('pet-bundle')) customElements.define('pet-bundle', PetBundle);

  /* ---------- <pet-scroller>: carrusel horizontal de tarjetas con flechas ---------- */
  class PetScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroll-track]');
      this.prev = this.querySelector('[data-scroll-prev]');
      this.next = this.querySelector('[data-scroll-next]');
      if (!this.track) return;
      const step = () => Math.max(this.track.clientWidth * 0.85, 200);
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
        this.classList.toggle('is-static', max <= 0);
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
    }
  }
  if (!customElements.get('pet-scroller')) customElements.define('pet-scroller', PetScroller);

  /* ---------- Formato de precio compartido ---------- */
  function formatMoney(cents, format) {
    const value = (Number(cents || 0) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const dots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const commas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '{{amount_with_comma_separator}} €')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${dots},${dec}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, dots)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, commas)
      .replace(/\{\{\s*amount\s*\}\}/, `${commas}.${dec}`);
  }

  /* ---------- <pet-packs>: packs de ahorro que cambian la cantidad ---------- */
  class PetPacks extends HTMLElement {
    connectedCallback() {
      this.format = this.dataset.moneyFormat;
      this.unit = parseInt(this.dataset.unit, 10) || 0;
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.apply(e.target);
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (variant && String(event.data.sectionId) === this.dataset.section) {
            this.unit = variant.price;
            this.renderPrices();
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.unsub) this.unsub();
    }

    apply(radio) {
      this.querySelectorAll('.pet-packs__option').forEach((o) => o.classList.toggle('is-selected', o.contains(radio)));
      const qty = document.getElementById(`Quantity-${this.dataset.section}`);
      if (qty) {
        qty.value = radio.value;
        qty.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      // Sin selector de cantidad: campo oculto en el formulario de compra
      const form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!form) return;
      let hidden = form.querySelector('input[name="quantity"]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        form.appendChild(hidden);
      }
      hidden.value = radio.value;
    }

    renderPrices() {
      this.querySelectorAll('.pet-packs__option').forEach((o) => {
        const input = o.querySelector('input');
        const qty = parseInt(input.value, 10);
        const disc = parseFloat(input.dataset.discount) || 0;
        const full = this.unit * qty;
        const final = disc > 0 ? Math.floor((full * (100 - disc)) / 100) : full;
        const price = o.querySelector('[data-pack-price]');
        if (price) price.textContent = formatMoney(final, this.format);
        const was = o.querySelector('[data-pack-was]');
        if (was) was.innerHTML = `<s>${formatMoney(full, this.format)}</s> · -${disc} %`;
        const unitEl = o.querySelector('[data-pack-unit]');
        if (unitEl) unitEl.textContent = `${formatMoney(this.unit, this.format)}/ud.`;
      });
    }
  }
  if (!customElements.get('pet-packs')) customElements.define('pet-packs', PetPacks);

  /* ---------- "+ Añadir" rápido (carrito lateral) ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-pet-quick-add]');
    if (!button) return;
    event.preventDefault();
    const cart = document.querySelector('cart-drawer');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: button.dataset.petQuickAdd, quantity: 1 }],
          sections: cart ? cart.getSectionsToRender().map((s) => s.id) : [],
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'Error');
      if (cart && typeof cart.renderContents === 'function') cart.renderContents(data);
      else window.location.href = window.routes.cart_url;
    } catch (e) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = 'Reintentar';
    }
  });

  /* ---------- Entrega estimada: "Pídelo antes de las 14:00 y recíbelo…" ---------- */
  function addBusinessDays(date, days) {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) added++;
    }
    return d;
  }
  function renderEta() {
    document.querySelectorAll('[data-pet-eta]').forEach((el) => {
      const cutoff = parseInt(el.dataset.cutoff, 10) || 14;
      const min = parseInt(el.dataset.min, 10) || 1;
      const max = parseInt(el.dataset.max, 10) || 2;
      const now = new Date();
      let start = new Date(now);
      const weekend = now.getDay() === 0 || now.getDay() === 6;
      if (weekend || now.getHours() >= cutoff) start = addBusinessDays(now, 1);
      const from = addBusinessDays(start, min);
      const to = addBusinessDays(start, max);
      const fmt = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
      const range = min === max ? `el <strong>${fmt.format(from)}</strong>` : `entre el <strong>${fmt.format(from)}</strong> y el <strong>${fmt.format(to)}</strong>`;
      const when = !weekend && now.getHours() < cutoff ? `Pídelo antes de las ${cutoff}:00 y recíbelo ${range}` : `Recíbelo ${range}`;
      el.innerHTML = when;
      el.hidden = false;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderEta);
  else renderEta();
  document.addEventListener('shopify:section:load', renderEta);

  /* ---------- Cuenta atrás de campañas (banner de temporada) ---------- */
  function startCountdowns() {
    document.querySelectorAll('[data-countdown]').forEach((el) => {
      if (el.dataset.cdStarted) return;
      const end = new Date(el.dataset.countdown).getTime();
      if (isNaN(end)) return;
      el.dataset.cdStarted = '1';
      const parts = { d: el.querySelector('[data-cd-d]'), h: el.querySelector('[data-cd-h]'), m: el.querySelector('[data-cd-m]'), s: el.querySelector('[data-cd-s]') };
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const left = end - Date.now();
        if (left <= 0) {
          el.hidden = true;
          clearInterval(timer);
          return;
        }
        const d = Math.floor(left / 86400000);
        const h = Math.floor((left % 86400000) / 3600000);
        const m = Math.floor((left % 3600000) / 60000);
        const s = Math.floor((left % 60000) / 1000);
        parts.d.textContent = d;
        parts.h.textContent = pad(h);
        parts.m.textContent = pad(m);
        parts.s.textContent = pad(s);
        el.hidden = false;
      };
      const timer = setInterval(tick, 1000);
      tick();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startCountdowns);
  else startCountdowns();
  document.addEventListener('shopify:section:load', startCountdowns);

  /* ---------- <pet-formats>: tarjetas de formato que eligen la variante ---------- */
  class PetFormats extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const card = e.target.closest('.pet-formats__card');
        if (card && card.dataset.pick) this.pick(card);
        else if (card) this.select(card);
      });
      this.addEventListener('change', (e) => {
        if (e.target.matches('.pet-pick select')) e.target.dataset.touched = '1';
      });
      // Con 2 opciones: al cambiar de variante, copia este bloque y el título ya actualizados del HTML de Shopify
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || !data.html || data.sectionId !== this.dataset.section) return;
          const fresh = data.html.querySelector('pet-formats');
          // en packs combinables no se repinta el bloque (se perderían los champús elegidos): solo el título
          if (fresh && this.classList.contains('pet-formats--two')) this.innerHTML = fresh.innerHTML;
          const info = this.closest('product-info') || document;
          const title = data.html.querySelector('.product__title');
          const mine = info.querySelector('.product__title');
          if (title && mine) mine.innerHTML = title.innerHTML;
          if (this.classList.contains('pet-formats--picks')) this.petTitle();
          // Botón «· Ahorras X €» y «Descuento aplicado automáticamente» (estilo Grüns)
          const sid = this.dataset.section;
          for (const id of [`ProductSubmitButton-${sid}`, `PetAutoApplied-${sid}`]) {
            const src = data.html.getElementById(id.replace(sid, data.sectionId)) || data.html.getElementById(id);
            const dst = document.getElementById(id);
            if (!src || !dst) continue;
            if (id.startsWith('Product')) { const a = src.querySelector('span'), b = dst.querySelector('span'); if (a && b) b.innerHTML = a.innerHTML; }
            else { dst.hidden = src.hidden; dst.innerHTML = src.innerHTML; }
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.unsub) this.unsub();
    }

    // Packs combinables: la tarjeta de champú elegida rellena el bote 1 (y los demás mientras no se hayan tocado)
    // Título según champú y formato (packs combinables)
    petTitle() {
      const h1 = (this.closest('product-info') || document).querySelector('.product__title h1');
      const card = this.querySelector('.pet-choice__card.is-active');
      const row = this.querySelector('.pet-row.is-active');
      if (!h1 || !card) return;
      const q = row ? +row.dataset.qty || 1 : 1;
      h1.textContent = q > 1 ? `Pack de ${q} champús para perros 250 ml · Combínalos como quieras` : `${card.dataset.title || card.dataset.pick} 250 ml`;
    }

    // Foto principal del champú elegido (la primera de la galería cuyo texto alternativo lo nombra)
    petPhoto(name) {
      const info = this.closest('product-info') || document;
      const gallery = info.querySelector('media-gallery');
      if (!gallery || !gallery.setActiveMedia) return;
      const li = [...gallery.querySelectorAll('[data-media-id]')].find((el) => (el.querySelector('img')?.alt || '').toLowerCase().includes(`champú ${name.toLowerCase()}`));
      if (li) { window.__petNoScroll = Date.now(); gallery.setActiveMedia(li.dataset.mediaId, false); }
    }

    pick(card) {
      this.petPhoto(card.dataset.pick);
      this.querySelectorAll('.pet-choice__card').forEach((c) => {
        c.classList.toggle('is-active', c === card);
        c.setAttribute('aria-checked', c === card ? 'true' : 'false');
      });
      this.querySelectorAll('.pet-pick select').forEach((sel, i) => {
        if (i === 0 || !sel.dataset.touched) sel.value = card.dataset.pick;
      });
      this.petTitle();
    }

    select(card) {
      // Al cambiar de formato, Shopify sube la página hasta la foto de la variante: aquí no, el cliente sigue eligiendo abajo
      window.__petNoScroll = Date.now();
      const value = card.dataset.value;
      const picker = document.getElementById(`variant-selects-${this.dataset.section}`);
      if (picker) {
        const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find((i) => i.value === value);
        const select = picker.querySelector('select');
        if (radio && !radio.checked) {
          radio.click();
        } else if (!radio && select && select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      // Packs combinables: enseñar tantos desplegables como unidades tiene el formato
      if (card.dataset.qty) {
        const q = +card.dataset.qty;
        this.querySelectorAll('.pet-pick').forEach((l) => {
          const on = +l.dataset.n <= q;
          l.hidden = !on;
          const sel = l.querySelector('select');
          if (sel) sel.disabled = !on;
        });
        const wrap = this.querySelector('.pet-picks-wrap');
        if (wrap) wrap.hidden = q < 2;
        const t = this.querySelector('[data-pet-picks-title]');
        if (t) t.textContent = `¿Qué ${q} quieres? Puedes repetir o combinar`;
      }
      (card.closest('.pet-formats__grid, .pet-rows') || this).querySelectorAll('.pet-formats__card').forEach((c) => {
        const on = c === card;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      const label = this.querySelector('[data-pet-formats-current]');
      if (label) label.textContent = value;

    }
  }
  if (!window.__petScrollPatched) {
    window.__petScrollPatched = true;
    const block = (fn) => function (...args) {
      if (window.__petNoScroll && Date.now() - window.__petNoScroll < 2000) return;
      return fn.apply(this, args);
    };
    window.scrollTo = block(window.scrollTo.bind(window));
    window.scroll = block(window.scroll.bind(window));
  }
  if (!customElements.get('pet-formats')) customElements.define('pet-formats', PetFormats);
})();

/* ---------- <pet-ai>: resumen + asistente estilo Rufus. Respuestas = metacampo custom.asistente (escritas con los datos
   del producto) + preguntas frecuentes y ficha técnica de la página. No inventa: si no sabe, lo dice. ---------- */
(function () {
  if (customElements.get('pet-ai')) return;
  const STOP = new Set(['que', 'qué', 'para', 'con', 'los', 'las', 'del', 'una', 'uno', 'hay', 'como', 'cómo', 'este', 'esta', 'esto', 'mi', 'mis', 'por', 'sus', 'son', 'est', 'tiene', 'puedo', 'pued', 'se', 'lo', 'la', 'el', 'es', 'y', 'o', 'de', 'en', 'a']);
  const norm = (t) => (t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9ñ ]/g, ' ');
  const words = (t) => norm(t).split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w));
  const stem = (w) => w.slice(0, Math.max(4, Math.min(6, w.length - 1)));
  class PetAi extends HTMLElement {
    connectedCallback() {
      let qa = [];
      try { qa = JSON.parse(this.querySelector('.pet-ai__data')?.textContent || 'null') || []; } catch (e) { qa = []; }
      qa = qa.map((x) => ({ ...x, k: (x.k || []).map(norm) }));
      document.querySelectorAll('.collapsible-content details').forEach((d) => {
        const q = d.querySelector('summary')?.innerText.trim();
        const a = d.querySelector('.accordion__content')?.innerText.trim();
        if (q && a && !qa.some((x) => x.q === q)) qa.push({ q, a, k: [] });
      });
      document.querySelectorAll('.pet-specs__row').forEach((r) => {
        const dt = r.querySelector('dt')?.innerText.trim(), dd = r.querySelector('dd')?.innerText.trim();
        if (dt && dd) qa.push({ q: dt, a: `${dt}: ${dd}.`, k: [], spec: true });
      });
      if (!qa.length) return;
      this.qa = qa;
      const qs = this.querySelector('.pet-ai__qs');
      qa.filter((x) => !x.spec).slice(0, 4).forEach((x) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'pet-ai__q'; b.textContent = x.q;
        b.addEventListener('click', () => { this.answer(x.q, x.a); b.disabled = true; b.classList.add('is-done'); });
        qs.appendChild(b);
      });
      this.querySelector('.pet-ai__form')?.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = e.target.querySelector('input');
        const text = input.value.trim();
        if (!text) return;
        input.value = '';
        this.answer(text, this.find(text));
      });
      this.querySelector('.pet-ai__ask').hidden = false;
    }
    find(text) {
      const ws = words(text);
      const nt = norm(text);
      let best = null, score = 0;
      for (const x of this.qa) {
        let sc = 0;
        for (const k of x.k) if (k && nt.includes(k)) sc += 4;
        const qw = words(x.q).map(stem), aw = norm(x.a);
        for (const w of ws) { const st = stem(w); if (qw.some((q) => q === st || q.startsWith(st) || st.startsWith(q))) sc += 3; else if (aw.includes(st)) sc += 1; }
        if (x.spec) sc -= 1;
        if (sc > score) { score = sc; best = x; }
      }
      if (best && score >= 3) return best.a;
      return 'Esa duda no la tengo en la ficha y no quiero decirte algo que no sea cierto. Escríbenos desde la página de contacto y te respondemos lo antes posible.';
    }
    answer(q, a) {
      const el = this.querySelector('.pet-ai__answer');
      el.hidden = false;
      const pq = document.createElement('p'); pq.className = 'pet-ai__you'; pq.textContent = q;
      const pa = document.createElement('p'); pa.className = 'pet-ai__bot'; pa.textContent = a;
      el.append(pq, pa);
      el.scrollTop = el.scrollHeight;
    }
  }
  customElements.define('pet-ai', PetAi);
})();

/* ---------- <pet-subscribe>: Compra única / Suscríbete y ahorra ---------- */
(function () {
  if (customElements.get('pet-subscribe')) return;
  class PetSubscribe extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type=radio]')) this.set(e.target.value);
        if (e.target.matches('[data-sub-select]')) { this.querySelector('input[value=sub]').checked = true; this.set('sub'); }
      });
      this.addEventListener('click', (e) => { if (e.target.matches('[data-sub-select]')) e.stopPropagation(); });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const fresh = data && data.html && data.html.querySelector('pet-subscribe');
          if (!fresh) return;
          const mode = this.querySelector('input[type=radio]:checked')?.value, freq = this.querySelector('[data-sub-select]')?.value;
          this.innerHTML = fresh.innerHTML;
          if (freq) { const s = this.querySelector('[data-sub-select]'); if (s) s.value = freq; }
          if (mode === 'sub') { this.querySelector('input[value=sub]').checked = true; this.set('sub'); }
        });
      }
    }
    disconnectedCallback() { if (this.unsub) this.unsub(); }
    set(mode) {
      this.querySelectorAll('.pet-sub__opt').forEach((o) => o.classList.toggle('is-active', o.dataset.sub === mode));
      const input = this.querySelector('[data-sub-input]');
      input.value = mode === 'sub' ? this.querySelector('[data-sub-select]').value : '';
    }
  }
  customElements.define('pet-subscribe', PetSubscribe);
})();

/* ---------- <pet-quiz>: test de 3 preguntas → producto recomendado ---------- */
(function () {
  if (customElements.get('pet-quiz')) return;
  const money = (c) => (c / 100).toFixed(2).replace('.', ',') + ' €';
  class PetQuiz extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('.pet-quiz__step')];
      this.answers = {};
      try { this.rules = JSON.parse(this.querySelector('[data-rules]').textContent); } catch (e) { this.rules = []; }
      this.addEventListener('click', (e) => {
        const opt = e.target.closest('.pet-quiz__opt');
        if (opt) { const st = opt.closest('.pet-quiz__step'); this.answers[st.dataset.key] = opt.dataset.value; this.go(+st.dataset.step + 1); }
        if (e.target.closest('[data-back]')) this.go(+e.target.closest('.pet-quiz__step').dataset.step - 1);
        if (e.target.closest('[data-restart]')) { this.answers = {}; this.go(0); }
        const add = e.target.closest('[data-add]');
        if (add) this.add(add);
      });
      this.bar(0);
    }
    bar(i) { const b = this.querySelector('.pet-quiz__bar span'); if (b) b.style.width = Math.round((i / this.steps.length) * 100) + '%'; }
    go(i) {
      this.steps.forEach((s, k) => (s.hidden = k !== i));
      this.querySelector('.pet-quiz__result').hidden = i < this.steps.length;
      this.bar(i);
      if (i >= this.steps.length) this.result();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    pick() {
      const ok = (r) => (r.when || '').split(',').map((c) => c.trim()).filter(Boolean).every((c) => { const [k, v] = c.split('='); return this.answers[k.trim()] === (v || '').trim(); });
      return this.rules.find(ok) || this.rules[this.rules.length - 1];
    }
    async card(handle, big) {
      const r = await fetch(`/products/${handle}.js`); if (!r.ok) return '';
      const p = await r.json(); const v = p.variants.find((x) => x.available) || p.variants[0];
      const img = (p.featured_image || '').replace(/(\.[a-z]+)(\?|$)/i, '_600x$1$2');
      return `<div class="pet-quiz__pcard${big ? ' is-big' : ''}"><a href="${p.url}"><img src="${img}" alt="${p.title.replace(/"/g, '')}" loading="lazy"></a><div><a class="pet-quiz__ptitle" href="${p.url}">${p.title}</a><p class="pet-quiz__pprice">desde ${money(p.price_min)}</p><div class="pet-quiz__pbtns"><button type="button" class="button button--primary" data-add="${v.id}">Añadir al carrito</button><a class="button button--secondary" href="${p.url}">Ver producto</a></div></div></div>`;
    }
    async result() {
      const r = this.pick(); if (!r) return;
      this.querySelector('[data-why]').textContent = r.why || '';
      const main = this.querySelector('[data-main]'); main.innerHTML = '<p class="pet-quiz__loading">Buscando el suyo…</p>';
      main.innerHTML = await this.card(r.handle, true);
      const ex = this.dataset.extra;
      if (ex && ex !== r.handle) { this.querySelector('[data-extra-prod]').innerHTML = await this.card(ex, false); this.querySelector('[data-extra-box]').hidden = false; }
    }
    async add(btn) {
      btn.disabled = true; btn.textContent = 'Añadiendo…';
      await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: +btn.dataset.add, quantity: 1 }] }) });
      window.location.href = '/cart';
    }
  }
  customElements.define('pet-quiz', PetQuiz);
})();

/* ---------- Aviso «regalo sorpresa + envío gratis» de la ficha: se actualiza al añadir al carrito ---------- */
(function () {
  const fmt = (c) => (c / 100).toFixed(2).replace('.', ',') + ' €';
  const refresh = async () => {
    const notes = document.querySelectorAll('.pet-gift-note[data-min]');
    if (!notes.length) return;
    const cart = await (await fetch('/cart.js')).json();
    notes.forEach((n) => {
      const min = +n.dataset.min, left = min - cart.total_price, t = n.querySelector('[data-gift-text]');
      if (!t) return;
      t.innerHTML = cart.item_count === 0 ? `Por compras superiores a <strong>${fmt(min)}</strong>: <strong>envío gratis + regalo sorpresa</strong>`
        : left > 0 ? `Te faltan <strong>${fmt(left)}</strong> para el <strong>regalo sorpresa y el envío gratis</strong>`
        : '¡Ya tienes <strong>regalo sorpresa y envío gratis</strong>!';
    });
  };
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
  document.addEventListener('DOMContentLoaded', () => { if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, refresh); });
})();

/* ---------- Regalo sorpresa: se añade solo al carrito al pasar del mínimo (29,99 €) y se quita si baja ---------- */
(function () {
  const GIFT = 'regalo-sorpresa';
  let busy = false;
  const min = () => Math.round(parseFloat(String(window.petFreeShippingMin || '29,99').replace(',', '.')) * 100);
  async function sync() {
    if (busy) return;
    busy = true;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const gift = cart.items.find((i) => i.handle === GIFT);
      const others = cart.items.filter((i) => i.handle !== GIFT);
      const total = others.reduce((s, i) => s + i.final_line_price, 0);
      let changed = false;
      if (total >= min() && others.length && !gift) {
        const p = await (await fetch(`/products/${GIFT}.js`)).json();
        await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: p.variants[0].id, quantity: 1 }] }) });
        changed = true;
      } else if (gift && (total < min() || gift.quantity !== 1)) {
        await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: gift.key, quantity: total >= min() ? 1 : 0 }) });
        changed = true;
      }
      if (changed) {
        if (location.pathname.startsWith('/cart')) { location.reload(); return; }
        const r = await (await fetch(`${location.pathname}?sections=cart-drawer,cart-icon-bubble`)).json();
        const doc = (h) => new DOMParser().parseFromString(h, 'text/html');
        const drawer = document.querySelector('cart-drawer');
        if (drawer && r['cart-drawer']) { const n = doc(r['cart-drawer']).querySelector('cart-drawer'); if (n) { drawer.innerHTML = n.innerHTML; drawer.classList.toggle('is-empty', n.classList.contains('is-empty')); } }
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && r['cart-icon-bubble']) { const n = doc(r['cart-icon-bubble']).querySelector('.shopify-section'); if (n) bubble.innerHTML = n.innerHTML; }
      }
    } catch (e) {} finally { busy = false; }
  }
  const hook = () => { if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(sync, 300)); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { hook(); sync(); }); else { hook(); sync(); }
})();
